import { useEffect } from 'react'
import { GUIDES } from '../../lib/hubCopy'
import { useHubLang } from '../../lib/i18n'
import { groupGuides, guideAskContext, guideHref, guideSectionDomId, type GuideRow } from '../../lib/guides'
import { GuideBody } from './GuideBody'

/**
 * #/help/guides - THE DOCS LAYOUT (339, HLP-45; One and Help v2 Mockup D).
 *
 *   list (For your roles / For everyone / Other teams' guides) | the guide | On this page
 *
 * When the PAGE is narrow (a phone, or a wide window with the menu open and
 * Ask One docked - HLP-49, container queries on `.guides__frame`) the list
 * becomes a picker at the top and On this page folds into a disclosure; other teams' guides fold away
 * under the picker, closed. Every guide is a route, and so is every section -
 * #/help/guides/store-manager/handover - so a desk reply or One's source line
 * (S5) can point at a paragraph. The foot's Ask One button opens the panel
 * with this guide and section as its chip (S5).
 *
 * 353 (D5 revised, David 1 Nov late): a role guide is read only by the people
 * who can open its pages, plus support-desk holders and tenant administrators -
 * so "Other teams' guides" only ever reaches those readers. There is NO
 * "Showing all guides" switch (349); a role guide says who it is for ("For
 * store managers") so a reader who is not one knows why it reads as it does.
 *
 * Takes everything as props and fetches nothing, so the suite renders it
 * whole (062). The route (routes/Guides.tsx) loads `guides()`. Which guides
 * arrive is the SERVER's decision; this file groups them and never re-judges
 * a role.
 */
export function GuidesPage({
  rows,
  current,
  section = null,
  onPick,
  onAskOne,
  error = null,
}: {
  /** null = still loading. */
  rows: GuideRow[] | null
  /** The guide in the URL, if any. */
  current: string | null
  section?: string | null
  /** The phone picker's choice: a guide key, or a guide/section path. */
  onPick: (path: string) => void
  /** Opens Ask One with this guide and section as the chip (S5). */
  onAskOne: (context: ReturnType<typeof guideAskContext>) => void
  error?: string | null
}) {
  const t = GUIDES[useHubLang()]
  const guide = rows && current ? rows.find((g) => g.key === current) ?? null : null
  const shown = guide ?? (rows && !current ? rows[0] ?? null : null)

  // A section route scrolls its section into view. Does nothing under
  // renderToString, which is correct.
  useEffect(() => {
    if (!shown || !section) return
    document.getElementById(guideSectionDomId(shown.key, section))?.scrollIntoView({ block: 'start' })
  }, [shown, section])

  if (error) return <p className="empty" role="alert">{t.loadFailed}</p>
  if (rows === null) return <p className="empty" aria-busy="true">{t.loading}</p>

  const groups = groupGuides(rows)
  const list = (title: string, gs: GuideRow[]) =>
    gs.length > 0 && (
      <div className="guides__group">
        <p className="guides__grouphead">{title}{gs.length > 1 && ` · ${gs.length}`}</p>
        <ul>
          {gs.map((g) => (
            <li key={g.key}>
              <a
                href={guideHref(g.key)}
                className={`guides__link${shown?.key === g.key ? ' guides__link--on' : ''}`}
                aria-current={shown?.key === g.key ? 'page' : undefined}
              >
                {g.title}
              </a>
              {shown?.key === g.key && (
                <ul className="guides__sections">
                  {g.sections.map((s) => (
                    <li key={s.key}>
                      <a href={guideHref(g.key, s.key)} aria-current={section === s.key ? 'location' : undefined}>
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    )

  // The phone picker lists your guides; another team's appears in it only
  // while it is the one open, so the select always holds its own value.
  const pickable = [...groups.roles, ...groups.everyone]
  const shownIsOther = !!shown && groups.others.some((g) => g.key === shown.key)

  return (
    // HLP-49: the frame is the size container the folds answer (hub.css).
    <div className="guides__frame">
    <div className="guides">
      <nav className="guides__nav" aria-label={t.title}>
        {list(t.forRoles, groups.roles)}
        {list(t.forEveryone, groups.everyone)}
        {list(t.otherRoles, groups.others)}
      </nav>

      <div className="guides__picker">
        <label>
          <span className="guides__pickerlabel">{t.pickGuide}</span>
          <select value={shown?.key ?? ''} onChange={(e) => onPick(e.target.value)}>
            {rows.length === 0 && <option value="">{t.title}</option>}
            {groups.roles.length > 0 && (
              <optgroup label={t.forRoles}>
                {groups.roles.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
              </optgroup>
            )}
            {groups.everyone.length > 0 && (
              <optgroup label={t.forEveryone}>
                {groups.everyone.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
              </optgroup>
            )}
            {shownIsOther && (
              <optgroup label={t.otherRoles}>
                <option value={shown!.key}>{shown!.title}</option>
              </optgroup>
            )}
            {pickable.length === 0 && !shownIsOther && rows.length > 0 && <option value="">{t.title}</option>}
          </select>
        </label>
        {/* 349. Other teams' guides, closed on a phone: readable by anyone
            (D5), and not what most people came for. */}
        {groups.others.length > 0 && (
          <details className="guides__others">
            <summary>{t.othersFold(groups.others.length)}</summary>
            <ul>
              {groups.others.map((g) => (
                <li key={g.key}>
                  <a href={guideHref(g.key)} aria-current={shown?.key === g.key ? 'page' : undefined}>{g.title}</a>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <article className="guides__main">
        {!shown ? (
          <p className="empty">{rows.length === 0 ? t.none : t.notYours}</p>
        ) : (
          <>
            {current && !guide && <p className="empty" role="status">{t.notYours}</p>}
            <p className="guides__crumbs">
              <a href="#/help/guides">{t.title}</a> › {shown.yours ? (shown.audience === 'everyone' ? t.forEveryone : t.forRoles) : t.otherRoles}
            </p>
            <h2 className="guides__title">{shown.title}</h2>
            <p className="guide__lede">{shown.lede}</p>
            {/* 349. Who the guide is FOR, said, so a reader who is not one
                knows why it reads as it does - and whether it is theirs. */}
            {shown.audience !== 'everyone' && (
              <div className="guides__why" role="note">
                <p><b>{t.forWho[shown.audience]}</b> · {shown.yours ? t.youHoldIt : t.youDoNotHoldIt}</p>
                <p>{t.relevance}</p>
              </div>
            )}
            <details className="guides__tocfold">
              <summary>{t.onThisPage}</summary>
              <Toc g={shown} section={section} />
            </details>
            <div className="guide">
              {shown.sections.map((s, i) => (
                <section key={s.key} id={guideSectionDomId(shown.key, s.key)} className="guide__section" lang={s.locale}>
                  <h3 className="guide__h">
                    <span className="guide__num">{String(i + 1).padStart(2, '0')}</span>
                    {s.title}
                  </h3>
                  <GuideBody body={s.body} locale={s.locale} />
                </section>
              ))}
            </div>
            <div className="guides__ask">
              <button type="button" className="btn btn--ai btn--sm" onClick={() => onAskOne(guideAskContext(shown, section))}>
                {t.askAbout}
              </button>
              <p className="guides__asknote">{t.askNote}</p>
            </div>
          </>
        )}
      </article>

      {shown && (
        <aside className="guides__toc" aria-label={t.onThisPage}>
          <p className="guides__grouphead">{t.onThisPage}</p>
          <Toc g={shown} section={section} />
        </aside>
      )}
    </div>
    </div>
  )
}

function Toc({ g, section }: { g: GuideRow; section: string | null }) {
  return (
    <ol>
      {g.sections.map((s) => (
        <li key={s.key}>
          <a href={guideHref(g.key, s.key)} aria-current={section === s.key ? 'location' : undefined}>
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  )
}
